/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import * as AppointmentActions from '@appointments/store/appointment.actions';
import { getCurrentAppointment, isAppointmentActionPending } from '@appointments/store/appointment.selectors';
import { State } from '@core/store';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { Appointment } from '@shared/models/appointment';

@Component({
    selector: 'app-appointment-detail',
    templateUrl: './appointment-detail.component.html',
    styleUrls: ['./appointment-detail.component.scss'],
    standalone: false
})
export class AppointmentDetailComponent implements OnInit, OnDestroy {
  readonly today = new Date(new Date().setHours(0, 0, 0, 0));
  appointmentSubscription: Subscription;
  queryParamsSubscription: Subscription;
  actionPending$: Observable<boolean>;
  appointmentForm: UntypedFormGroup;

  constructor(
    private store: Store<State>,
    private formBuilder: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    private datePipe: DatePipe
  ) {
    this.appointmentForm = this.formBuilder.group({
      '@id': [null],
      date: ['', Validators.required],
      time: ['', Validators.required],
      participants: ['', Validators.required],
      address: ['', Validators.required],
      customer: ['', Validators.required],
      note: ['']
    });
  }

  ngOnInit(): void {
    this.actionPending$ = this.store.select(isAppointmentActionPending);

    this.appointmentSubscription = this.activatedRoute.params.pipe(
      tap(params => this.store.dispatch(new AppointmentActions.LoadAppointment(params.appointmentId))),
      switchMap(() => this.store.select(getCurrentAppointment)),
      filter(appointment => !!appointment && !(Object.keys(appointment).length === 0)),
  ).subscribe(
      appointment => this.appointmentForm.patchValue({
      ...appointment,
      date: this.datePipe.transform(new Date(appointment.date), 'yyyy-MM-dd'),
      time: this.datePipe.transform(new Date(appointment.date), 'HH:mm')
    }));

    this.queryParamsSubscription = this.activatedRoute.queryParams.subscribe(params =>  {
      if (Object.prototype.hasOwnProperty.call(params, 'date')) {
        this.appointmentForm.get('date').patchValue(new Date(params.date));
      }
    });
  }

  ngOnDestroy(): void {
    this.appointmentSubscription.unsubscribe();
    this.queryParamsSubscription.unsubscribe();
  }

  persistAppointment(): void {
    this.store.dispatch(new AppointmentActions.PersistAppointment({
      ...this.appointmentForm.value,
      date: `${new Date(this.appointmentForm.value.date).toDateString()} ${this.appointmentForm.value.time}`
    }));
  }
}
